import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calculator, FileSpreadsheet, AlertTriangle } from 'lucide-react';
import { api, ApiCallError } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/sonner';

interface TenantRuc {
  id: string;
  ruc: string;
  razonSocial: string;
  isPrimary: boolean;
}

interface IvaSummary {
  periodYear: number;
  periodMonth: number;
  dueDate: string;
  invoicesIncluded: string[];
  totalsByDirection: {
    purchase: { count: number; ivaExempt: number; iva5: number; iva10: number; grandTotal: number };
    sale: { count: number; ivaExempt: number; iva5: number; iva10: number; grandTotal: number };
  };
  ivaCredit: number;
  ivaDebit: number;
  netToPay: number;
  saldoAFavor: number;
}

interface PreviewResponse {
  summary: IvaSummary;
  approvedPendingCount: number;
}

interface Props {
  tenantId: string;
  rucs: TenantRuc[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function IvaDeclarationDialog({ tenantId, rucs, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth());
  const [rucId, setRucId] = useState<string>('');

  useEffect(() => {
    if (!rucId && rucs.length > 0) {
      const primary = rucs.find((r) => r.isPrimary) ?? rucs[0];
      if (primary) setRucId(primary.id);
    }
  }, [rucs, rucId]);

  const previewQuery = useQuery({
    queryKey: ['iva-preview', tenantId, rucId, year, month],
    queryFn: () =>
      api<PreviewResponse>('/api/admin/declarations/iva/preview', {
        method: 'POST',
        body: JSON.stringify({ tenantId, tenantRucId: rucId, year, month }),
      }),
    enabled: open && !!rucId,
  });

  const generateMutation = useMutation({
    mutationFn: () =>
      api<{ declaration: { id: string } }>('/api/admin/declarations/iva/generate', {
        method: 'POST',
        body: JSON.stringify({ tenantId, tenantRucId: rucId, year, month, ifExists: 'error' }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
      qc.invalidateQueries({ queryKey: ['tenant', tenantId, 'declarations'] });
      toast.success('Declaración IVA generada en estado borrador');
      onOpenChange(false);
    },
    onError: (err) => {
      if (err instanceof ApiCallError) {
        if (err.body.error === 'no_invoices_in_period') {
          toast.error('No hay facturas aprobadas en el período');
        } else if (err.body.error === 'declaration_already_exists') {
          toast.error('Ya existe una declaración para ese período');
        } else {
          toast.error(`No se pudo generar: ${err.body.error}`);
        }
      } else {
        toast.error('No se pudo generar la declaración');
      }
    },
  });

  const summary = previewQuery.data?.summary;
  const noInvoices = summary && summary.invoicesIncluded.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] w-[95vw] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet />
            Generar declaración IVA (F.120)
          </DialogTitle>
          <DialogDescription>
            Calcula el IVA del período sumando las facturas aprobadas, y crea una declaración
            en estado borrador para que un contador la revise antes de presentar a la SET.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label required>RUC</Label>
              <Select value={rucId} onValueChange={setRucId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Elegí RUC…" />
                </SelectTrigger>
                <SelectContent>
                  {rucs.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.ruc} — {r.razonSocial}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label required>Año</Label>
              <Input
                type="number"
                min={2020}
                max={2100}
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
              />
            </div>
            <div className="space-y-1.5">
              <Label required>Mes</Label>
              <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <SelectItem key={m} value={String(m)}>
                      {new Date(2020, m - 1, 1).toLocaleString('es-PY', { month: 'long' })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {previewQuery.isLoading && (
            <p className="text-sm text-muted-foreground">Calculando…</p>
          )}

          {summary && (
            <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Calculator className="size-4" />
                Resumen del período
              </div>

              {noInvoices ? (
                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <AlertTriangle className="size-4 text-warning mt-0.5" />
                  <p>
                    No hay facturas aprobadas en el período. Aprobá facturas desde la cola de
                    revisión antes de generar la declaración.
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                    <Row label="Compras">
                      {summary.totalsByDirection.purchase.count} facturas
                    </Row>
                    <Row label="Ventas">{summary.totalsByDirection.sale.count} facturas</Row>
                    <Row label="IVA crédito (compras)">
                      <span className="font-mono">
                        Gs {summary.ivaCredit.toLocaleString('es-PY')}
                      </span>
                    </Row>
                    <Row label="IVA débito (ventas)">
                      <span className="font-mono">
                        Gs {summary.ivaDebit.toLocaleString('es-PY')}
                      </span>
                    </Row>
                  </div>

                  <div className="border-t border-border pt-3 flex items-center justify-between">
                    {summary.netToPay > 0 ? (
                      <>
                        <span className="text-sm font-medium">A pagar:</span>
                        <span className="text-lg font-bold font-mono">
                          Gs {summary.netToPay.toLocaleString('es-PY')}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-sm font-medium">Saldo a favor:</span>
                        <Badge variant="success">
                          Gs {summary.saldoAFavor.toLocaleString('es-PY')}
                        </Badge>
                      </>
                    )}
                  </div>

                  <div className="text-xs text-muted-foreground">
                    Vencimiento estimado: {new Date(summary.dueDate + 'T12:00:00').toLocaleDateString('es-PY')}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending || !summary || noInvoices || !rucId}
          >
            {generateMutation.isPending ? 'Generando…' : 'Generar declaración'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span>{children}</span>
    </div>
  );
}
